/** @format */

import {
	Box,
	Card,
	Chip,
	Divider,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Tab,
	Tabs,
	Alert,
	Avatar,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	SelectChangeEvent,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import OutlinedInput from "@mui/material/OutlinedInput";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import GroupIcon from "@mui/icons-material/Group";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import ClearIcon from "@mui/icons-material/Clear";
import React, { useEffect, useState, useCallback } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import {
	getPlatformFeeRevenue,
	getStripeAccountBalance,
	LateFeeRecord,
	PayoutFeeRecord,
	FeeRevenueByCircle,
	FeeRevenueByMonth,
} from "../../../redux/actions/revenueAction";
import {
	selectFeeRevenue,
	selectStripeBalance,
	selectIsLoadingFeeRevenue,
	selectIsLoadingStripeBalance,
	selectFeeRevenueError,
	selectStripeBalanceError,
	selectFeeRevenueSummary,
	selectLateFeeRecords,
	selectPayoutFeeRecords,
	selectFeeRevenueByMonth,
	selectFeeRevenueByCircle,
	selectFeeRevenueByMember,
	selectRevenueLastRefreshedAt,
} from "../../../redux/selectors/revenueSelector";
import { clearRevenueErrors } from "../../../redux/reducers/revenueReducer";
import { LoadingSpinner } from "../../allComponents/AllComponents";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";
import { formatCurrency } from "../../../utility/currencyUtils";

// Tab types
type TabValue =
	| "overview"
	| "lateFees"
	| "payoutFees"
	| "byCircle"
	| "byMember"
	| "byMonth";

// Filter types
type FilterMode = "all" | "past" | "today" | "future" | "custom";

const RevenueReports = () => {
	const dispatch = useDispatch<AppDispatch>();

	// Selectors
	const feeRevenue = useSelector(selectFeeRevenue);
	const stripeBalance = useSelector(selectStripeBalance);
	const isLoadingFeeRevenue = useSelector(selectIsLoadingFeeRevenue);
	const isLoadingStripeBalance = useSelector(selectIsLoadingStripeBalance);
	const feeRevenueError = useSelector(selectFeeRevenueError);
	const stripeBalanceError = useSelector(selectStripeBalanceError);
	const summary = useSelector(selectFeeRevenueSummary);
	const lateFeeRecords = useSelector(selectLateFeeRecords);
	const payoutFeeRecords = useSelector(selectPayoutFeeRecords);
	const byMonth = useSelector(selectFeeRevenueByMonth);
	const byCircle = useSelector(selectFeeRevenueByCircle);
	const byMember = useSelector(selectFeeRevenueByMember);
	const lastRefreshedAt = useSelector(selectRevenueLastRefreshedAt);

	// UI State
	const [openNav, setOpenNav] = useState(false);
	const [activeTab, setActiveTab] = useState<TabValue>("overview");
	const [searchQuery, setSearchQuery] = useState("");
	const [dismissedError, setDismissedError] = useState(false);

	// Filter State
	const [filterMode, setFilterMode] = useState<FilterMode>("all");
	const [startDate, setStartDate] = useState<Dayjs | null>(null);
	const [endDate, setEndDate] = useState<Dayjs | null>(null);
	const [showCalendar, setShowCalendar] = useState(false);
	const [calendarDate, setCalendarDate] = useState<Dayjs | null>(null);

	// Pagination state
	const [lateFeeePage, setLateFeePage] = useState(0);
	const [lateFeeRowsPerPage, setLateFeeRowsPerPage] = useState(10);
	const [payoutFeePage, setPayoutFeePage] = useState(0);
	const [payoutFeeRowsPerPage, setPayoutFeeRowsPerPage] = useState(10);
	const [circlePage, setCirclePage] = useState(0);
	const [circleRowsPerPage, setCircleRowsPerPage] = useState(10);
	const [memberPage, setMemberPage] = useState(0);
	const [memberRowsPerPage, setMemberRowsPerPage] = useState(10);

	// Fetch data
	const fetchRevenueData = useCallback(() => {
		const params: {
			filter?: "all" | "past" | "today" | "future";
			startDate?: string;
			endDate?: string;
		} = {};

		if (filterMode === "custom" && startDate && endDate) {
			params.startDate = startDate.format("YYYY-MM-DD");
			params.endDate = endDate.format("YYYY-MM-DD");
		} else if (filterMode !== "all" && filterMode !== "custom") {
			params.filter = filterMode;
		}

		dispatch(getPlatformFeeRevenue(params));
		dispatch(getStripeAccountBalance());
	}, [dispatch, filterMode, startDate, endDate]);

	useEffect(() => {
		fetchRevenueData();
	}, [fetchRevenueData]);

	const handleTabChange = (
		_event: React.SyntheticEvent,
		newValue: TabValue,
	) => {
		setActiveTab(newValue);
		setSearchQuery("");
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
	};

	const handleRefresh = () => {
		setDismissedError(false);
		dispatch(clearRevenueErrors());
		fetchRevenueData();
	};

	const handleFilterChange = (event: SelectChangeEvent<FilterMode>) => {
		const newFilter = event.target.value as FilterMode;
		setFilterMode(newFilter);
		if (newFilter !== "custom") {
			setStartDate(null);
			setEndDate(null);
		}
	};

	const handleCalendarDateSelect = (date: Dayjs | null) => {
		if (date) {
			setCalendarDate(date);
			setFilterMode("custom");
			setStartDate(date.startOf("day"));
			setEndDate(date.endOf("day"));
		}
	};

	const handleClearFilters = () => {
		setFilterMode("all");
		setStartDate(null);
		setEndDate(null);
		setCalendarDate(null);
	};

	// Format currency (from cents)
	const formatCurrencyFromCents = (amountCents: number) => {
		return formatCurrency(amountCents / 100, "usd");
	};

	// Filter late fees based on search query
	const filterLateFees = (records: LateFeeRecord[]): LateFeeRecord[] => {
		if (!searchQuery) return records;
		const query = searchQuery.toLowerCase();
		return records.filter((record) => {
			const memberName = (record.memberName || "").toLowerCase();
			const memberEmail = (record.memberEmail || "").toLowerCase();
			const circleName = (record.circleName || "").toLowerCase();
			return (
				memberName.includes(query) ||
				memberEmail.includes(query) ||
				circleName.includes(query)
			);
		});
	};

	// Filter payout fees based on search query
	const filterPayoutFees = (records: PayoutFeeRecord[]): PayoutFeeRecord[] => {
		if (!searchQuery) return records;
		const query = searchQuery.toLowerCase();
		return records.filter((record) => {
			const memberName = (record.memberName || "").toLowerCase();
			const memberEmail = (record.memberEmail || "").toLowerCase();
			const circleName = (record.circleName || "").toLowerCase();
			return (
				memberName.includes(query) ||
				memberEmail.includes(query) ||
				circleName.includes(query)
			);
		});
	};

	// Render Overview Tab
	const renderOverviewTab = () => {
		return (
			<Stack spacing={3}>
				{/* Stripe Balance Cards */}
				<Typography variant="h6">Platform Balance</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<Card
							sx={{
								p: 2,
								height: "100%",
								background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
								color: "white",
							}}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "rgba(255,255,255,0.2)",
									}}>
									<AccountBalanceWalletIcon sx={{ color: "white" }} />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold">
										{stripeBalance?.availableFormatted || "$0.00"}
									</Typography>
									<Typography variant="body2" sx={{ opacity: 0.9 }}>
										Available Balance
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card
							sx={{
								p: 2,
								height: "100%",
								background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
								color: "white",
							}}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "rgba(255,255,255,0.2)",
									}}>
									<ScheduleIcon sx={{ color: "white" }} />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold">
										{stripeBalance?.pendingFormatted || "$0.00"}
									</Typography>
									<Typography variant="body2" sx={{ opacity: 0.9 }}>
										Pending Balance
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card
							sx={{
								p: 2,
								height: "100%",
								background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
								color: "white",
							}}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "rgba(255,255,255,0.2)",
									}}>
									<AttachMoneyIcon sx={{ color: "white" }} />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold">
										{stripeBalance?.totalFormatted || "$0.00"}
									</Typography>
									<Typography variant="body2" sx={{ opacity: 0.9 }}>
										Total Balance
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "success.lighter",
									}}>
									<TrendingUpIcon color="success" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="success.main">
										{summary?.grandTotal?.dollars || "$0.00"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Fee Revenue
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{/* Fee Revenue Summary */}
				<Typography variant="h6" sx={{ mt: 2 }}>
					Fee Revenue Breakdown
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "warning.lighter",
									}}>
									<MoneyOffIcon color="warning" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="warning.main">
										{summary?.lateFees?.totalDollars || "$0.00"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Late Fees ({summary?.lateFees?.count || 0} transactions)
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "info.lighter",
									}}>
									<PaymentsIcon color="info" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold" color="info.main">
										{summary?.payoutFees?.totalDollars || "$0.00"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Payout Fees ({summary?.payoutFees?.count || 0} transactions)
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "primary.lighter",
									}}>
									<ReceiptLongIcon color="primary" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="primary.main">
										{(summary?.lateFees?.count || 0) +
											(summary?.payoutFees?.count || 0)}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Transactions
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{/* Revenue by Month Chart/Cards */}
				<Typography variant="h6" sx={{ mt: 2 }}>
					Revenue by Month
				</Typography>
				<Grid container spacing={2}>
					{!byMonth || byMonth.length === 0 ?
						<Grid item xs={12}>
							<Card sx={{ p: 4, textAlign: "center" }}>
								<Typography variant="body2" color="text.secondary">
									No monthly data available
								</Typography>
							</Card>
						</Grid>
						: byMonth.slice(0, 6).map((month: FeeRevenueByMonth) => (
							<Grid item xs={12} sm={6} md={4} lg={2} key={month.monthKey}>
								<Card
									sx={{
										p: 2,
										height: "100%",
										transition: "all 0.2s ease-in-out",
										"&:hover": {
											transform: "translateY(-2px)",
											boxShadow: 4,
										},
									}}>
									<Stack spacing={1}>
										<Typography
											variant="subtitle2"
											color="text.secondary"
											noWrap>
											{month.monthLabel}
										</Typography>
										<Typography
											variant="h6"
											fontWeight="bold"
											color="success.main">
											{formatCurrencyFromCents(month.totalCents)}
										</Typography>
										<Divider />
										<Stack direction="row" justifyContent="space-between">
											<Typography variant="caption" color="warning.main">
												Late: {formatCurrencyFromCents(month.lateFeeCents)}
											</Typography>
										</Stack>
										<Stack direction="row" justifyContent="space-between">
											<Typography variant="caption" color="info.main">
												Payout: {formatCurrencyFromCents(month.payoutFeeCents)}
											</Typography>
										</Stack>
									</Stack>
								</Card>
							</Grid>
						))
					}
				</Grid>

				{/* Top Circles by Revenue */}
				<Typography variant="h6" sx={{ mt: 2 }}>
					Top Circles by Revenue
				</Typography>
				<Grid container spacing={2}>
					{!byCircle || byCircle.length === 0 ?
						<Grid item xs={12}>
							<Card sx={{ p: 4, textAlign: "center" }}>
								<Typography variant="body2" color="text.secondary">
									No circle data available
								</Typography>
							</Card>
						</Grid>
						: byCircle.slice(0, 6).map((circle: FeeRevenueByCircle) => (
							<Grid item xs={12} sm={6} md={4} key={circle.circleId}>
								<Card
									sx={{
										p: 2,
										height: "100%",
										transition: "all 0.2s ease-in-out",
										"&:hover": {
											transform: "translateY(-2px)",
											boxShadow: 4,
										},
									}}>
									<Stack spacing={2}>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center">
											<Typography variant="subtitle1" fontWeight="bold" noWrap>
												{circle.circleName}
											</Typography>
											<Chip
												label={formatCurrencyFromCents(circle.totalCents)}
												size="small"
												color="success"
											/>
										</Stack>
										<Divider />
										<Grid container spacing={1}>
											<Grid item xs={6}>
												<Stack direction="row" spacing={1} alignItems="center">
													<MoneyOffIcon fontSize="small" color="warning" />
													<Typography variant="body2">
														{formatCurrencyFromCents(circle.lateFeeCents)}
													</Typography>
												</Stack>
											</Grid>
											<Grid item xs={6}>
												<Stack direction="row" spacing={1} alignItems="center">
													<PaymentsIcon fontSize="small" color="info" />
													<Typography variant="body2">
														{formatCurrencyFromCents(circle.payoutFeeCents)}
													</Typography>
												</Stack>
											</Grid>
										</Grid>
									</Stack>
								</Card>
							</Grid>
						))
					}
				</Grid>
			</Stack>
		);
	};

	// Render Late Fees Tab
	const renderLateFeesTab = () => {
		const records = lateFeeRecords || [];
		const filteredRecords = filterLateFees(records);
		const paginatedRecords = filteredRecords.slice(
			lateFeeePage * lateFeeRowsPerPage,
			lateFeeePage * lateFeeRowsPerPage + lateFeeRowsPerPage,
		);

		return (
			<Stack spacing={3}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<MoneyOffIcon color="warning" />
								<Box>
									<Typography variant="h5" color="warning.main">
										{records.length}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Late Fee Transactions
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<AttachMoneyIcon color="success" />
								<Box>
									<Typography variant="h5" color="success.main">
										{summary?.lateFees?.totalDollars || "$0.00"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Late Fees Collected
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "1000px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Amount</TableCell>
									<TableCell>Missed Dates</TableCell>
									<TableCell>Fee %</TableCell>
									<TableCell>Date</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedRecords.length === 0 ?
									<TableRow>
										<TableCell colSpan={7} align="center">
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ py: 4 }}>
												No late fee records found
											</Typography>
										</TableCell>
									</TableRow>
									: paginatedRecords.map((record, index) => (
										<TableRow hover key={`${record.id}-${index}`}>
											<TableCell>
												<Stack direction="row" spacing={1} alignItems="center">
													<Avatar sx={{ width: 32, height: 32 }}>
														{record.memberName?.charAt(0) || "?"}
													</Avatar>
													<Typography variant="subtitle2">
														{record.memberName || "N/A"}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{record.memberEmail || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{record.circleName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={record.amountDollars}
													size="small"
													color="warning"
												/>
											</TableCell>
											<TableCell>
												<Tooltip title={record.missedDates || "N/A"}>
													<Chip
														label={`${record.missedDatesCount} date(s)`}
														size="small"
														variant="outlined"
													/>
												</Tooltip>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{record.feePercentage}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{record.dateFormatted}
												</Typography>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={filteredRecords.length}
						onPageChange={(_, newPage) => setLateFeePage(newPage)}
						onRowsPerPageChange={(e) => {
							setLateFeeRowsPerPage(parseInt(e.target.value, 10));
							setLateFeePage(0);
						}}
						page={lateFeeePage}
						rowsPerPage={lateFeeRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render Payout Fees Tab
	const renderPayoutFeesTab = () => {
		const records = payoutFeeRecords || [];
		const filteredRecords = filterPayoutFees(records);
		const paginatedRecords = filteredRecords.slice(
			payoutFeePage * payoutFeeRowsPerPage,
			payoutFeePage * payoutFeeRowsPerPage + payoutFeeRowsPerPage,
		);

		return (
			<Stack spacing={3}>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<PaymentsIcon color="info" />
								<Box>
									<Typography variant="h5" color="info.main">
										{records.length}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Payout Fee Transactions
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={4}>
						<Card sx={{ p: 2 }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<AttachMoneyIcon color="success" />
								<Box>
									<Typography variant="h5" color="success.main">
										{summary?.payoutFees?.totalDollars || "$0.00"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Payout Fees Collected
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "1200px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Fee Amount</TableCell>
									<TableCell>Gross Payout</TableCell>
									<TableCell>Net Payout</TableCell>
									<TableCell>Fee %</TableCell>
									<TableCell>Date</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedRecords.length === 0 ?
									<TableRow>
										<TableCell colSpan={8} align="center">
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ py: 4 }}>
												No payout fee records found
											</Typography>
										</TableCell>
									</TableRow>
									: paginatedRecords.map((record, index) => (
										<TableRow hover key={`${record.id}-${index}`}>
											<TableCell>
												<Stack direction="row" spacing={1} alignItems="center">
													<Avatar sx={{ width: 32, height: 32 }}>
														{record.memberName?.charAt(0) || "?"}
													</Avatar>
													<Typography variant="subtitle2">
														{record.memberName || "N/A"}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{record.memberEmail || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{record.circleName || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={record.feeDollars}
													size="small"
													color="info"
												/>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{record.grossPayoutDollars}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="medium">
													{record.netPayoutDollars}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{record.feePercentage}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{record.dateFormatted}
												</Typography>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={filteredRecords.length}
						onPageChange={(_, newPage) => setPayoutFeePage(newPage)}
						onRowsPerPageChange={(e) => {
							setPayoutFeeRowsPerPage(parseInt(e.target.value, 10));
							setPayoutFeePage(0);
						}}
						page={payoutFeePage}
						rowsPerPage={payoutFeeRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render By Circle Tab
	const renderByCircleTab = () => {
		const circles = byCircle || [];
		const paginatedCircles = circles.slice(
			circlePage * circleRowsPerPage,
			circlePage * circleRowsPerPage + circleRowsPerPage,
		);

		return (
			<Stack spacing={3}>
				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "800px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Circle Name</TableCell>
									<TableCell align="right">Late Fees</TableCell>
									<TableCell align="right">Payout Fees</TableCell>
									<TableCell align="right">Total Revenue</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedCircles.length === 0 ?
									<TableRow>
										<TableCell colSpan={4} align="center">
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ py: 4 }}>
												No circle data found
											</Typography>
										</TableCell>
									</TableRow>
									: paginatedCircles.map((circle, index) => (
										<TableRow hover key={`${circle.circleId}-${index}`}>
											<TableCell>
												<Stack direction="row" spacing={2} alignItems="center">
													<Avatar sx={{ bgcolor: "primary.main" }}>
														<GroupIcon />
													</Avatar>
													<Typography variant="subtitle2">
														{circle.circleName || "N/A"}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={formatCurrencyFromCents(circle.lateFeeCents)}
													size="small"
													color="warning"
													variant="outlined"
												/>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={formatCurrencyFromCents(circle.payoutFeeCents)}
													size="small"
													color="info"
													variant="outlined"
												/>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={formatCurrencyFromCents(circle.totalCents)}
													size="small"
													color="success"
												/>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={circles.length}
						onPageChange={(_, newPage) => setCirclePage(newPage)}
						onRowsPerPageChange={(e) => {
							setCircleRowsPerPage(parseInt(e.target.value, 10));
							setCirclePage(0);
						}}
						page={circlePage}
						rowsPerPage={circleRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render By Member Tab
	const renderByMemberTab = () => {
		const members = byMember || [];
		const paginatedMembers = members.slice(
			memberPage * memberRowsPerPage,
			memberPage * memberRowsPerPage + memberRowsPerPage,
		);

		return (
			<Stack spacing={3}>
				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "900px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell align="right">Late Fees</TableCell>
									<TableCell align="right">Payout Fees</TableCell>
									<TableCell align="right">Total Fees</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedMembers.length === 0 ?
									<TableRow>
										<TableCell colSpan={5} align="center">
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ py: 4 }}>
												No member data found
											</Typography>
										</TableCell>
									</TableRow>
									: paginatedMembers.map((member, index) => (
										<TableRow hover key={`${member.memberId}-${index}`}>
											<TableCell>
												<Stack direction="row" spacing={2} alignItems="center">
													<Avatar>{member.memberName?.charAt(0) || "?"}</Avatar>
													<Typography variant="subtitle2">
														{member.memberName || "N/A"}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{member.memberEmail || "N/A"}
												</Typography>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={formatCurrencyFromCents(member.lateFeeCents)}
													size="small"
													color="warning"
													variant="outlined"
												/>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={formatCurrencyFromCents(member.payoutFeeCents)}
													size="small"
													color="info"
													variant="outlined"
												/>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={formatCurrencyFromCents(member.totalCents)}
													size="small"
													color="success"
												/>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={members.length}
						onPageChange={(_, newPage) => setMemberPage(newPage)}
						onRowsPerPageChange={(e) => {
							setMemberRowsPerPage(parseInt(e.target.value, 10));
							setMemberPage(0);
						}}
						page={memberPage}
						rowsPerPage={memberRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render By Month Tab
	const renderByMonthTab = () => {
		const months = byMonth || [];

		return (
			<Stack spacing={3}>
				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "700px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Month</TableCell>
									<TableCell align="right">Late Fees</TableCell>
									<TableCell align="right">Payout Fees</TableCell>
									<TableCell align="right">Total Revenue</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{months.length === 0 ?
									<TableRow>
										<TableCell colSpan={4} align="center">
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ py: 4 }}>
												No monthly data found
											</Typography>
										</TableCell>
									</TableRow>
									: months.map((month, index) => (
										<TableRow hover key={`${month.monthKey}-${index}`}>
											<TableCell>
												<Stack direction="row" spacing={2} alignItems="center">
													<Avatar sx={{ bgcolor: "primary.main" }}>
														<CalendarMonthIcon />
													</Avatar>
													<Typography variant="subtitle2">
														{month.monthLabel}
													</Typography>
												</Stack>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={formatCurrencyFromCents(month.lateFeeCents)}
													size="small"
													color="warning"
													variant="outlined"
												/>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={formatCurrencyFromCents(month.payoutFeeCents)}
													size="small"
													color="info"
													variant="outlined"
												/>
											</TableCell>
											<TableCell align="right">
												<Chip
													label={formatCurrencyFromCents(month.totalCents)}
													size="small"
													color="success"
												/>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
				</Card>
			</Stack>
		);
	};

	const isLoading = isLoadingFeeRevenue || isLoadingStripeBalance;
	const error = feeRevenueError || stripeBalanceError;

	return (
		<React.Fragment>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
			<Box
				sx={{
					minHeight: 1,
					display: "flex",
					flexDirection: { xs: "column", lg: "row" },
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				<Main>
					{isLoadingFeeRevenue && !feeRevenue ?
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								minHeight: "60vh",
							}}>
							<LoadingSpinner loading={isLoadingFeeRevenue} size={45} />
						</Box>
						: <Stack spacing={3}>
							{/* Header */}
							<Stack direction="row" spacing={3}>
								<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
									<Typography variant="h4">Revenue Reports</Typography>
									<Typography color="text.secondary" variant="body2">
										View platform fee revenue including late fees and payout
										fees
									</Typography>
								</Stack>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<Tooltip title="Refresh">
										<span>
											<IconButton onClick={handleRefresh} disabled={isLoading}>
												{isLoading ?
													<CircularProgress size={24} />
													: <ArrowsClockwise />}
											</IconButton>
										</span>
									</Tooltip>
								</Box>
							</Stack>

							{/* Error Alert */}
							{error && !dismissedError && (
								<Alert severity="error" onClose={() => setDismissedError(true)}>
									{error}
								</Alert>
							)}

							{/* Filter Bar */}
							<Card sx={{ p: 2 }}>
								<Grid container spacing={2} alignItems="center">
									<Grid item xs={12} md={3}>
										<OutlinedInput
											value={searchQuery}
											onChange={handleSearchChange}
											fullWidth
											size="small"
											placeholder="Search by member, email, or circle..."
											startAdornment={
												<InputAdornment position="start">
													<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
												</InputAdornment>
											}
										/>
									</Grid>
									<Grid item xs={12} md={2}>
										<FormControl fullWidth size="small">
											<InputLabel>Filter</InputLabel>
											<Select
												value={filterMode}
												label="Filter"
												onChange={handleFilterChange}>
												<MenuItem value="all">All Time</MenuItem>
												<MenuItem value="past">Past</MenuItem>
												<MenuItem value="today">Today</MenuItem>
												<MenuItem value="future">Future</MenuItem>
												<MenuItem value="custom">Custom Range</MenuItem>
											</Select>
										</FormControl>
									</Grid>
									{filterMode === "custom" && (
										<>
											<Grid item xs={12} md={2}>
												<LocalizationProvider dateAdapter={AdapterDayjs}>
													<DatePicker
														label="Start Date"
														value={startDate}
														onChange={(date) => setStartDate(date)}
														slotProps={{
															textField: { size: "small", fullWidth: true },
														}}
													/>
												</LocalizationProvider>
											</Grid>
											<Grid item xs={12} md={2}>
												<LocalizationProvider dateAdapter={AdapterDayjs}>
													<DatePicker
														label="End Date"
														value={endDate}
														onChange={(date) => setEndDate(date)}
														slotProps={{
															textField: { size: "small", fullWidth: true },
														}}
													/>
												</LocalizationProvider>
											</Grid>
										</>
									)}
									<Grid item xs={12} md={filterMode === "custom" ? 3 : 5}>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
											justifyContent="flex-end">
											{filterMode !== "all" && (
												<Button
													variant="outlined"
													size="small"
													startIcon={<ClearIcon />}
													onClick={handleClearFilters}>
													Clear Filters
												</Button>
											)}
											{feeRevenue?.filter && (
												<Chip
													label={feeRevenue.filter.label}
													color="primary"
													size="small"
													variant="outlined"
												/>
											)}
											{lastRefreshedAt && (
												<Typography variant="caption" color="text.secondary">
													Last updated:{" "}
													{dayjs(lastRefreshedAt).format("HH:mm:ss")}
												</Typography>
											)}
										</Stack>
									</Grid>
								</Grid>
							</Card>

							{/* Calendar Quick Filter */}
							<Card sx={{ p: 2 }}>
								<Stack
									direction="row"
									spacing={2}
									alignItems="center"
									justifyContent="space-between">
									<Stack direction="row" spacing={2} alignItems="center">
										<CalendarMonthIcon color="primary" />
										<Typography variant="subtitle2">
											Quick Date Filter
										</Typography>
										<Button
											variant={showCalendar ? "contained" : "outlined"}
											size="small"
											onClick={() => setShowCalendar(!showCalendar)}>
											{showCalendar ? "Hide Calendar" : "Show Calendar"}
										</Button>
									</Stack>
									{calendarDate && (
										<Chip
											label={`Selected: ${calendarDate.format("MMM D, YYYY")}`}
											onDelete={() => {
												setCalendarDate(null);
												handleClearFilters();
											}}
											color="primary"
											size="small"
										/>
									)}
								</Stack>
								{showCalendar && (
									<Box
										sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
										<LocalizationProvider dateAdapter={AdapterDayjs}>
											<DateCalendar
												value={calendarDate}
												onChange={handleCalendarDateSelect}
												sx={{
													border: 1,
													borderColor: "divider",
													borderRadius: 2,
												}}
											/>
										</LocalizationProvider>
									</Box>
								)}
							</Card>

							{/* Tabs */}
							<Card>
								<Tabs
									value={activeTab}
									onChange={handleTabChange}
									indicatorColor="primary"
									textColor="primary"
									variant="scrollable"
									scrollButtons="auto">
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<TrendingUpIcon />
												<span>Overview</span>
											</Stack>
										}
										value="overview"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<MoneyOffIcon />
												<span>Late Fees</span>
												<Chip
													label={summary?.lateFees?.count || 0}
													size="small"
													color="warning"
												/>
											</Stack>
										}
										value="lateFees"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<PaymentsIcon />
												<span>Payout Fees</span>
												<Chip
													label={summary?.payoutFees?.count || 0}
													size="small"
													color="info"
												/>
											</Stack>
										}
										value="payoutFees"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<GroupIcon />
												<span>By Circle</span>
											</Stack>
										}
										value="byCircle"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<PersonIcon />
												<span>By Member</span>
											</Stack>
										}
										value="byMember"
									/>
									<Tab
										label={
											<Stack direction="row" spacing={1} alignItems="center">
												<CalendarMonthIcon />
												<span>By Month</span>
											</Stack>
										}
										value="byMonth"
									/>
								</Tabs>
							</Card>

							{/* Tab Content */}
							{isLoading ?
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										minHeight: 300,
									}}>
									<CircularProgress />
								</Box>
								: <>
									{activeTab === "overview" && renderOverviewTab()}
									{activeTab === "lateFees" && renderLateFeesTab()}
									{activeTab === "payoutFees" && renderPayoutFeesTab()}
									{activeTab === "byCircle" && renderByCircleTab()}
									{activeTab === "byMember" && renderByMemberTab()}
									{activeTab === "byMonth" && renderByMonthTab()}
								</>
							}
						</Stack>
					}
				</Main>
			</Box>
		</React.Fragment>
	);
};

export default RevenueReports;
